import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Users, MessageSquare, Building2, Briefcase, Mail,
  Loader2, RefreshCw, ShieldCheck, Calendar, Phone
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "../components/ui/tabs";
import {
  adminMe, adminStats, adminList, adminLogout, isAdminLoggedIn, adminUpdateStatus
} from "../services/api";
import { useToast } from "../hooks/use-toast";

const tabConfig = [
  { key: "admissions", label: "Admissions", icon: Users, color: "bg-orange-500" },
  { key: "contacts", label: "Contacts", icon: MessageSquare, color: "bg-amber-500" },
  { key: "franchise", label: "Franchise", icon: Building2, color: "bg-rose-500" },
  { key: "careers", label: "Careers", icon: Briefcase, color: "bg-teal-500" },
  { key: "newsletter", label: "Newsletter", icon: Mail, color: "bg-lime-600" }
];

const statusColors = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  resolved: "bg-green-100 text-green-700"
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState({});
  const [data, setData] = useState({});
  const [activeTab, setActiveTab] = useState("admissions");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await adminMe();
      const s = await adminStats();
      setStats(s);
      const results = await Promise.all(tabConfig.map((t) => adminList(t.key)));
      const dataMap = {};
      tabConfig.forEach((t, i) => { dataMap[t.key] = results[i]; });
      setData(dataMap);
    } catch (err) {
      if (err.response?.status === 401) {
        adminLogout();
        navigate("/admin");
      } else {
        toast({ title: "Failed to load data", description: err.message, variant: "destructive" });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    if (!isAdminLoggedIn()) { navigate("/admin"); return; }
    loadAll();
  }, [navigate, loadAll]);

  const logout = () => {
    adminLogout();
    toast({ title: "Signed out", description: "See you soon!" });
    navigate("/admin");
  };

  const changeStatus = async (type, id, newStatus) => {
    try {
      await adminUpdateStatus(type, id, newStatus);
      setData((prev) => ({
        ...prev,
        [type]: prev[type].map((item) => item.id === id ? { ...item, status: newStatus } : item)
      }));
      toast({ title: "Status updated", description: `Marked as ${newStatus}` });
    } catch (err) {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  const formatDate = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] bg-stone-50 py-8 md:py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center shadow-lg">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-stone-800">Admin Dashboard</h1>
              <p className="text-stone-500 text-sm">Aadi Gayanam Play School</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadAll} disabled={refreshing} variant="outline" className="rounded-full border-orange-200 text-orange-700 hover:bg-orange-50">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={logout} className="rounded-full bg-stone-800 hover:bg-stone-900 text-white">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {tabConfig.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`p-5 rounded-2xl border transition-all text-left ${
                  activeTab === t.key
                    ? "bg-white border-orange-300 shadow-lg"
                    : "bg-white border-orange-100 shadow-sm hover:shadow-md"
                }`}
              >
                <div className={`w-10 h-10 rounded-xl ${t.color} flex items-center justify-center mb-3 shadow`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="font-display font-bold text-2xl md:text-3xl text-stone-800">{stats[t.key] ?? 0}</p>
                <p className="text-xs text-stone-500 uppercase tracking-widest font-semibold">{t.label}</p>
              </button>
            );
          })}
        </div>

        {/* Data table */}
        <div className="bg-white rounded-2xl border border-orange-100 shadow-md p-4 md:p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-orange-50 mb-5 flex flex-wrap h-auto">
              {tabConfig.map((t) => (
                <TabsTrigger key={t.key} value={t.key} className="data-[state=active]:bg-orange-600 data-[state=active]:text-white rounded-full">
                  {t.label} ({data[t.key]?.length || 0})
                </TabsTrigger>
              ))}
            </TabsList>

            {tabConfig.map((t) => (
              <TabsContent key={t.key} value={t.key} className="mt-0">
                {(data[t.key] || []).length === 0 ? (
                  <div className="text-center py-16 text-stone-400">
                    <p className="text-sm">No {t.label.toLowerCase()} yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-orange-100 text-left">
                          <th className="py-3 px-3 font-semibold text-stone-700">Name / Email</th>
                          <th className="py-3 px-3 font-semibold text-stone-700 hidden md:table-cell">Details</th>
                          <th className="py-3 px-3 font-semibold text-stone-700 hidden md:table-cell">Date</th>
                          {t.key !== "newsletter" && <th className="py-3 px-3 font-semibold text-stone-700">Status</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(data[t.key] || []).map((item) => (
                          <tr key={item.id} className="border-b border-orange-50 hover:bg-orange-50/40">
                            <td className="py-4 px-3">
                              <p className="font-semibold text-stone-800">{item.name || item.parentName || item.email}</p>
                              <p className="text-xs text-stone-500 flex items-center gap-2 mt-0.5">
                                {item.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {item.email}</span>}
                                {item.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {item.phone}</span>}
                              </p>
                            </td>
                            <td className="py-4 px-3 hidden md:table-cell text-stone-600 max-w-xs">
                              {t.key === "admissions" && `${item.childName} (${item.childAge}) • ${item.program}`}
                              {t.key === "contacts" && <span className="line-clamp-2">{item.message}</span>}
                              {t.key === "franchise" && `${item.city} • ${item.investment}`}
                              {t.key === "careers" && item.role}
                              {t.key === "newsletter" && "Subscriber"}
                            </td>
                            <td className="py-4 px-3 hidden md:table-cell text-xs text-stone-500 whitespace-nowrap">
                              <Calendar className="inline w-3 h-3 mr-1" />
                              {formatDate(item.created_at)}
                            </td>
                            {t.key !== "newsletter" && (
                              <td className="py-4 px-3">
                                <select
                                  value={item.status || "new"}
                                  onChange={(e) => changeStatus(t.key, item.id, e.target.value)}
                                  className={`text-xs font-semibold px-2.5 py-1 rounded-full border-0 cursor-pointer ${statusColors[item.status || "new"]}`}
                                >
                                  <option value="new">New</option>
                                  <option value="contacted">Contacted</option>
                                  <option value="resolved">Resolved</option>
                                </select>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
